export const EVENT_CREATOR_EXPERIMENT_VARIANT = 'event-creator-ontology-v1'

/**
 * @typedef {'event_type' | 'goal' | 'mechanic' | 'participation' | 'device' | 'leads' | 'rewards' | 'scale' | 'integration'} EventPrimaryCategoryId
 * @typedef {'popular' | EventPrimaryCategoryId} EventCategoryId
 *
 * @typedef {Object} EventCategory
 * @property {EventCategoryId} id
 * @property {string} label
 * @property {number} order
 * @property {boolean=} isViewOnly
 *
 * @typedef {Object} EventTile
 * @property {string} id
 * @property {EventPrimaryCategoryId} categoryId
 * @property {string} label
 * @property {string=} shortLabel
 * @property {string=} description
 * @property {string=} icon
 * @property {boolean=} popular
 * @property {number=} popularOrder
 * @property {number} categoryOrder
 * @property {boolean} enabled
 * @property {string} analyticsKey
 * @property {string[]} semanticTags
 * @property {string[]} incompatibleWith
 * @property {string[]} recommendedWith
 * @property {Object.<string, string | number | boolean>} metadata
 */

/** @type {EventCategory[]} */
export const eventCategories = [
    { id: 'popular', label: 'Popular', order: 0, isViewOnly: true },
    { id: 'event_type', label: 'Type', order: 1 },
    { id: 'goal', label: 'Goal', order: 2 },
    { id: 'mechanic', label: 'Game type', order: 3 },
    { id: 'participation', label: 'Participation', order: 4 },
    { id: 'device', label: 'Devices', order: 5 },
    { id: 'leads', label: 'Leads', order: 6 },
    { id: 'rewards', label: 'Prizes', order: 7 },
    { id: 'scale', label: 'Scale', order: 8 },
    { id: 'integration', label: 'Integrations', order: 9 },
]

const LEAD_OPTIONS = [
    { id: 'leads_basic_contact', label: 'Name and email' },
    { id: 'leads_custom_form', label: 'Custom lead form' },
    { id: 'leads_before_game', label: 'Collect data before the game' },
    { id: 'leads_after_game', label: 'Collect data after the game' },
    { id: 'leads_consent', label: 'Consent or opt-in required' },
]

const REWARD_OPTIONS = [
    { id: 'rewards_instant_prize', label: 'Instant prize' },
    { id: 'rewards_prize_draw', label: 'Prize draw after the event' },
    { id: 'rewards_limited_inventory', label: 'Limited number of prizes' },
    { id: 'rewards_promo_code', label: 'Promo codes or coupons' },
    { id: 'rewards_winner_log', label: 'Save the list of winners' },
]

const LEAD_OPTION_IDS = LEAD_OPTIONS.map(option => option.id)
const REWARD_OPTION_IDS = REWARD_OPTIONS.map(option => option.id)

/**
 * Applies defaults required by the EventTile model.
 *
 * @param {Partial<EventTile> & Pick<EventTile, 'id' | 'categoryId' | 'label' | 'categoryOrder'>} config
 * @returns {EventTile}
 */
const createTile = config => ({
    enabled: true,
    analyticsKey: config.id,
    semanticTags: [],
    incompatibleWith: [],
    recommendedWith: [],
    metadata: {},
    ...config,
})

/** @type {EventTile[]} */
export const eventTiles = [
    createTile({
        id: 'venue_trade_show',
        categoryId: 'event_type',
        label: 'Trade show or booth',
        categoryOrder: 0,
        popular: true,
        popularOrder: 0,
        semanticTags: ['expo', 'booth', 'exhibition'],
    }),
    createTile({
        id: 'venue_conference',
        categoryId: 'event_type',
        label: 'Conference',
        categoryOrder: 1,
        semanticTags: ['conference', 'summit'],
    }),
    createTile({
        id: 'venue_in_store',
        categoryId: 'event_type',
        label: 'In-store activation',
        categoryOrder: 2,
        semanticTags: ['retail', 'store', 'promotion'],
    }),
    createTile({
        id: 'venue_corporate_event',
        categoryId: 'event_type',
        label: 'Corporate event',
        categoryOrder: 3,
        semanticTags: ['corporate', 'internal-event'],
    }),
    createTile({
        id: 'venue_public_event',
        categoryId: 'event_type',
        label: 'Festival or public event',
        categoryOrder: 4,
        semanticTags: ['festival', 'public-event'],
    }),
    createTile({
        id: 'venue_online_event',
        categoryId: 'event_type',
        label: 'Online or hybrid event',
        categoryOrder: 5,
        semanticTags: ['online', 'hybrid'],
    }),

    createTile({
        id: 'goal_engage_audience',
        categoryId: 'goal',
        label: 'Engage the audience',
        categoryOrder: 0,
        popular: true,
        popularOrder: 1,
        semanticTags: ['engagement'],
    }),
    createTile({
        id: 'goal_collect_leads',
        categoryId: 'goal',
        label: 'Collect leads',
        categoryOrder: 1,
        popular: true,
        popularOrder: 2,
        semanticTags: ['lead-generation'],
    }),
    createTile({
        id: 'goal_prize_giveaway',
        categoryId: 'goal',
        label: 'Give away prizes',
        categoryOrder: 2,
        popular: true,
        popularOrder: 4,
        semanticTags: ['giveaway', 'rewards'],
    }),
    createTile({
        id: 'goal_promote_product',
        categoryId: 'goal',
        label: 'Promote a product',
        categoryOrder: 3,
        semanticTags: ['product-promotion'],
    }),
    createTile({
        id: 'goal_educate_audience',
        categoryId: 'goal',
        label: 'Educate participants',
        categoryOrder: 4,
        semanticTags: ['education', 'knowledge'],
    }),
    createTile({
        id: 'goal_team_engagement',
        categoryId: 'goal',
        label: 'Engage employees or teams',
        categoryOrder: 5,
        semanticTags: ['employee-engagement', 'team-building'],
    }),

    createTile({
        id: 'mechanic_spin_wheel',
        categoryId: 'mechanic',
        label: 'Spin the Wheel',
        categoryOrder: 0,
        metadata: { interactyBlock: 'Spin the Wheel' },
    }),
    createTile({
        id: 'mechanic_quiz',
        categoryId: 'mechanic',
        label: 'Quiz or competition',
        categoryOrder: 1,
        popular: true,
        popularOrder: 6,
        metadata: { interactyBlock: 'Trivia Quiz' },
    }),
    createTile({
        id: 'mechanic_memory',
        categoryId: 'mechanic',
        label: 'Memory Game',
        categoryOrder: 2,
        metadata: { interactyBlock: 'Memory' },
    }),
    createTile({
        id: 'mechanic_instant_win',
        categoryId: 'mechanic',
        label: 'Instant Win',
        categoryOrder: 3,
        metadata: { interactyBlock: 'Reward-based project' },
    }),
    createTile({
        id: 'mechanic_leaderboard',
        categoryId: 'mechanic',
        label: 'Leaderboard competition',
        categoryOrder: 4,
        metadata: { interactyBlock: 'Quiz / Rank Battle' },
    }),
    createTile({
        id: 'mechanic_puzzle',
        categoryId: 'mechanic',
        label: 'Puzzle or challenge',
        categoryOrder: 5,
        metadata: { interactyBlock: 'Puzzle / Match Up / Hidden Objects' },
    }),

    createTile({
        id: 'participation_individual',
        categoryId: 'participation',
        label: 'One participant at a time',
        categoryOrder: 0,
    }),
    createTile({
        id: 'participation_walk_up',
        categoryId: 'participation',
        label: 'Walk-up self-service',
        categoryOrder: 1,
    }),
    createTile({
        id: 'participation_host_led',
        categoryId: 'participation',
        label: 'Game with a host',
        categoryOrder: 2,
    }),
    createTile({
        id: 'participation_participant_phones',
        categoryId: 'participation',
        label: 'Participants join by phone',
        categoryOrder: 3,
        popular: true,
        popularOrder: 7,
    }),
    createTile({
        id: 'participation_team_game',
        categoryId: 'participation',
        label: 'Teams compete',
        categoryOrder: 4,
    }),
    createTile({
        id: 'participation_continuous_flow',
        categoryId: 'participation',
        label: 'Continuous visitor flow',
        categoryOrder: 5,
    }),

    createTile({
        id: 'device_touchscreen',
        categoryId: 'device',
        label: 'Touchscreen',
        categoryOrder: 0,
        popular: true,
        popularOrder: 3,
    }),
    createTile({ id: 'device_tablet', categoryId: 'device', label: 'Tablet', categoryOrder: 1 }),
    createTile({ id: 'device_large_screen', categoryId: 'device', label: 'Large screen', categoryOrder: 2 }),
    createTile({ id: 'device_kiosk', categoryId: 'device', label: 'Self-service kiosk', categoryOrder: 3 }),
    createTile({
        id: 'device_participant_phones',
        categoryId: 'device',
        label: 'Participants’ phones',
        categoryOrder: 4,
    }),
    createTile({ id: 'device_desktop', categoryId: 'device', label: 'Laptop or desktop', categoryOrder: 5 }),

    createTile({
        id: 'leads_none',
        categoryId: 'leads',
        label: 'No participant data',
        categoryOrder: 0,
        incompatibleWith: LEAD_OPTION_IDS,
    }),
    ...LEAD_OPTIONS.map(({ id, label }, index) =>
        createTile({
            id,
            categoryId: 'leads',
            label,
            categoryOrder: index + 1,
            incompatibleWith: ['leads_none'],
        }),
    ),

    createTile({
        id: 'rewards_none',
        categoryId: 'rewards',
        label: 'No prizes',
        categoryOrder: 0,
        incompatibleWith: REWARD_OPTION_IDS,
    }),
    ...REWARD_OPTIONS.map(({ id, label }, index) =>
        createTile({
            id,
            categoryId: 'rewards',
            label,
            categoryOrder: index + 1,
            incompatibleWith: ['rewards_none'],
        }),
    ),

    createTile({
        id: 'scale_single_screen',
        categoryId: 'scale',
        label: 'One screen',
        categoryOrder: 0,
        incompatibleWith: ['scale_multiple_screens'],
    }),
    createTile({
        id: 'scale_multiple_screens',
        categoryId: 'scale',
        label: 'Multiple screens',
        categoryOrder: 1,
        popular: true,
        popularOrder: 5,
        incompatibleWith: ['scale_single_screen'],
    }),
    createTile({
        id: 'scale_single_event',
        categoryId: 'scale',
        label: 'One event',
        categoryOrder: 2,
        incompatibleWith: ['scale_event_series'],
    }),
    createTile({
        id: 'scale_event_series',
        categoryId: 'scale',
        label: 'Series of events',
        categoryOrder: 3,
        incompatibleWith: ['scale_single_event'],
    }),
    createTile({
        id: 'scale_multiple_locations',
        categoryId: 'scale',
        label: 'Multiple locations',
        categoryOrder: 4,
    }),
    createTile({
        id: 'scale_multiple_clients',
        categoryId: 'scale',
        label: 'Multiple clients',
        categoryOrder: 5,
    }),

    createTile({ id: 'integration_csv', categoryId: 'integration', label: 'Export to CSV', categoryOrder: 0 }),
    createTile({
        id: 'integration_embed',
        categoryId: 'integration',
        label: 'Embed on a website',
        categoryOrder: 1,
    }),
    createTile({ id: 'integration_crm', categoryId: 'integration', label: 'Send leads to CRM', categoryOrder: 2 }),
    createTile({
        id: 'integration_webhook',
        categoryId: 'integration',
        label: 'Webhook or API',
        categoryOrder: 3,
    }),
    createTile({
        id: 'integration_custom_domain',
        categoryId: 'integration',
        label: 'Custom domain',
        categoryOrder: 4,
    }),
    createTile({
        id: 'integration_external_analytics',
        categoryId: 'integration',
        label: 'External analytics',
        categoryOrder: 5,
    }),
]

export const getTilesForCategory = categoryId =>
    eventTiles
        .filter(tile => tile.enabled && (categoryId === 'popular' ? tile.popular : tile.categoryId === categoryId))
        .sort((firstTile, secondTile) =>
            categoryId === 'popular'
                ? firstTile.popularOrder - secondTile.popularOrder
                : firstTile.categoryOrder - secondTile.categoryOrder,
        )

export const getSelectedTiles = selectedTileIds => {
    const selectedIds = new Set(selectedTileIds)
    return eventTiles.filter(tile => selectedIds.has(tile.id))
}
